import React, { useState } from 'react';
import { ArrowRight, CheckCircle2, ChevronDown, ChevronRight, Clock3, Inbox, Loader2 } from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  formatQueueDateTime,
  getCommandCenterStateLabel,
  getCommandCenterWorkspaceLabel,
  getQueueItemNavigation,
  getSlackCaptureCardCopy,
} from './commandCenterQueueUtils';

function QueueMetaPill({ children }) {
  return (
    <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-medium text-slate-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-200">
      {children}
    </span>
  );
}

function ActiveActionCard({
  item,
  index,
  busy,
  onOpenItem,
  onEditItem,
  onResolveItem,
  onDeferItem,
}) {
  const hasExactDestination = Boolean(getQueueItemNavigation(item));

  return (
    <Card className="border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-[#171b20]">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-full bg-aqua/15 px-2 text-sm font-semibold text-cyan-900 dark:text-aqua">
                {index + 1}
              </span>
              <Badge className="border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-400/20 dark:bg-sky-400/10 dark:text-sky-200">
                {getCommandCenterWorkspaceLabel(item.workspace)}
              </Badge>
              {item.category ? (
                <Badge className="border-slate-200 bg-slate-50 text-slate-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-200">
                  {item.category}
                </Badge>
              ) : null}
            </div>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">{item.title || 'Untitled action'}</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
              {item.detail || item.rank_reason || 'No additional context yet.'}
            </p>
          </div>
          {busy ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : null}
        </div>

        <div className="flex flex-wrap gap-2">
          {item.rank_reason ? <QueueMetaPill>{item.rank_reason}</QueueMetaPill> : null}
          {item.suggested_position ? <QueueMetaPill>{item.suggested_position}</QueueMetaPill> : null}
          {item.due_at ? <QueueMetaPill>Due {formatQueueDateTime(item.due_at)}</QueueMetaPill> : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {hasExactDestination ? (
            <Button
              type="button"
              onClick={() => onOpenItem(item)}
              className="bg-aqua text-charcoal hover:bg-aqua-dark"
              disabled={busy}
            >
              Open
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button type="button" variant="outline" disabled className="border-slate-200 text-slate-500 dark:border-white/10 dark:text-slate-400">
              No destination yet
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => onEditItem(item)} disabled={busy}>
            Edit
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => onResolveItem(item)}
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-400/20 dark:text-emerald-300 dark:hover:bg-emerald-500/10"
            disabled={busy}
          >
            <CheckCircle2 className="mr-2 h-4 w-4" />
            Resolve
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => onDeferItem(item)}
            className="border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/[0.04]"
            disabled={busy}
          >
            <Clock3 className="mr-2 h-4 w-4" />
            Later
          </Button>
        </div>
      </div>
    </Card>
  );
}

function NeedsReviewCard({
  items,
  mutatingIds,
  onOpenItem,
  onEditItem,
  onPromoteItem,
  onSendLater,
  onDismissItem,
}) {
  const [expanded, setExpanded] = useState(false);
  if (!items.length) return null;
  const visibleItems = expanded ? items : items.slice(0, 1);
  const hasMoreThanOneItem = items.length > 1;

  return (
    <Card className="border-amber-200 bg-amber-50/75 p-5 dark:border-amber-400/15 dark:bg-amber-500/5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-amber-700 dark:text-amber-300">
            Needs Review
          </p>
          <h3 className="mt-2 text-lg font-semibold text-slate-900 dark:text-white">
            {items.length} {items.length === 1 ? 'item is' : 'items are'} waiting for a judgment call
          </h3>
          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
            These do not compete with the top three until you promote them into active work.
          </p>
        </div>
        <Badge className="border-amber-200 bg-white text-amber-700 dark:border-amber-400/20 dark:bg-black/20 dark:text-amber-200">
          {items.length} waiting
        </Badge>
      </div>

      {hasMoreThanOneItem ? (
        <div className="mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => setExpanded((current) => !current)}
            className="border-amber-200 bg-white text-amber-700 hover:bg-amber-100 dark:border-amber-400/20 dark:bg-black/20 dark:text-amber-200 dark:hover:bg-amber-400/10"
          >
            {expanded ? <ChevronDown className="mr-2 h-4 w-4" /> : <ChevronRight className="mr-2 h-4 w-4" />}
            {expanded ? 'Hide extra review items' : `Show all ${items.length} review items`}
          </Button>
        </div>
      ) : null}

      <div className="mt-4 grid gap-3">
        {visibleItems.map((item) => {
          const busy = Boolean(mutatingIds[item.queue_item_id]);
          const cardCopy = getSlackCaptureCardCopy(item);
          const hasExactDestination = Boolean(getQueueItemNavigation(item));
          return (
            <div
              key={item.queue_item_id}
              className="rounded-2xl border border-amber-200 bg-white/90 p-4 dark:border-amber-400/15 dark:bg-black/10"
            >
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="border-amber-200 bg-amber-100 text-amber-700 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-200">
                      {getCommandCenterWorkspaceLabel(item.workspace)}
                    </Badge>
                    <QueueMetaPill>{getCommandCenterStateLabel(item.state)}</QueueMetaPill>
                    {item.source_type === 'slack' ? <QueueMetaPill>Slack capture</QueueMetaPill> : null}
                  </div>
                  <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-white">{cardCopy.title}</p>
                  <p className="mt-1.5 text-sm leading-6 text-slate-600 dark:text-gray-300">
                    {cardCopy.preview}
                  </p>
                  {item.source_type === 'slack' ? (
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500 dark:text-gray-400">
                      {item.source_ref?.channel_name ? <span>Channel: {item.source_ref.channel_name}</span> : null}
                      {item.source_ref?.author_name ? <span>From: {item.source_ref.author_name}</span> : null}
                    </div>
                  ) : null}
                </div>
                {busy ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : null}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {hasExactDestination ? (
                  <Button type="button" variant="outline" onClick={() => onOpenItem(item)} disabled={busy}>
                    Open
                  </Button>
                ) : (
                  <Button type="button" variant="outline" disabled>
                    No destination yet
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={() => onEditItem(item)} disabled={busy}>
                  Edit
                </Button>
                <Button type="button" onClick={() => onPromoteItem(item)} className="bg-aqua text-charcoal hover:bg-aqua-dark" disabled={busy}>
                  Promote to active
                </Button>
                <Button type="button" variant="outline" onClick={() => onSendLater(item)} disabled={busy}>
                  Send to later
                </Button>
                <Button type="button" variant="outline" onClick={() => onDismissItem(item)} disabled={busy}>
                  Dismiss
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function ActionListSection({
  activeItems,
  needsReviewItems,
  loading,
  refreshing,
  error,
  creating,
  mutatingIds,
  onRefresh,
  onOpenQuickAdd,
  onOpenItem,
  onEditItem,
  onResolveItem,
  onDeferItem,
  onPromoteReviewItem,
  onSendReviewItemLater,
  onDismissReviewItem,
}) {
  const visibleActiveItems = activeItems.slice(0, 3);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-gray-400">Priority Board</p>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Open Action List</h3>
          <p className="text-sm text-slate-600 dark:text-gray-400">
            Cross-lane triage lives here. The three actions that deserve attention first sit on top, with the rest of the queue underneath.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" onClick={onOpenQuickAdd} className="bg-aqua text-charcoal hover:bg-aqua-dark" disabled={creating}>
            {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Quick Add
          </Button>
          <Button type="button" variant="outline" onClick={onRefresh} disabled={loading || refreshing}>
            {refreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Refresh
          </Button>
        </div>
      </div>

      <NeedsReviewCard
        items={needsReviewItems}
        mutatingIds={mutatingIds}
        onOpenItem={onOpenItem}
        onEditItem={onEditItem}
        onPromoteItem={onPromoteReviewItem}
        onSendLater={onSendReviewItemLater}
        onDismissItem={onDismissReviewItem}
      />

      {loading ? (
        <Card className="border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-[#171b20]">
          <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-gray-300">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading the durable queue.
          </div>
        </Card>
      ) : error ? (
        <Card className="border-red-200 bg-red-50 p-6 dark:border-red-400/20 dark:bg-red-500/5">
          <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
        </Card>
      ) : visibleActiveItems.length ? (
        <div className="grid gap-4 xl:grid-cols-3">
          {visibleActiveItems.map((item, index) => (
            <ActiveActionCard
              key={item.queue_item_id}
              item={item}
              index={index}
              busy={Boolean(mutatingIds[item.queue_item_id])}
              onOpenItem={onOpenItem}
              onEditItem={onEditItem}
              onResolveItem={onResolveItem}
              onDeferItem={onDeferItem}
            />
          ))}
        </div>
      ) : (
        <Card className="border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-[#171b20]">
          <div className="flex items-start gap-3">
            <div className="rounded-full border border-slate-200 bg-slate-50 p-2 dark:border-white/10 dark:bg-white/[0.04]">
              <Inbox className="h-4 w-4 text-slate-500 dark:text-slate-300" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-900 dark:text-white">No active queue items yet</h4>
              <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-gray-300">
                Add a task, review incoming items, or pull something back from Later when it deserves top attention.
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
